import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { useUpgradeNudge } from "@/lib/nudges/useUpgradeNudge.hook";
import { usePodWorkspaceStore } from "@/lib/podWorkspace/podWorkspace.store";

/**
 * Nudges a free user toward a trial exactly once, after their first podcast
 * finishes generating. Dismissible toast, never a blocking page; CTA opens
 * /pricing. Renders nothing.
 */
export function ActivationUpgradePrompt() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { eligible, canShow, markShown, markClicked, markDismissed } =
		useUpgradeNudge();
	const podcastsByWorkspace = usePodWorkspaceStore(
		(state) => state.podcastsByWorkspace,
	);
	const hasPromptedRef = useRef(false);

	useEffect(() => {
		if (!eligible || hasPromptedRef.current) return;
		if (!canShow("activation_podcast_ready")) return;

		const completedPodcast = Object.values(podcastsByWorkspace).find(
			(podcast) => podcast?.status === "COMPLETED",
		) as { id?: string } | undefined;
		if (!completedPodcast) return;

		hasPromptedRef.current = true;
		const context = {
			podcast_id: completedPodcast.id,
			trigger: "first_podcast_ready",
		};
		markShown("activation_podcast_ready", context);

		toast(t("activationPrompt.title"), {
			description: t("activationPrompt.description"),
			duration: 15000,
			action: {
				label: t("activationPrompt.viewPlans"),
				onClick: () => {
					markClicked("activation_podcast_ready", context);
					navigate("/pricing");
				},
			},
			cancel: {
				label: t("activationPrompt.dismiss"),
				onClick: () => {
					markDismissed("activation_podcast_ready", context);
				},
			},
		});
	}, [
		eligible,
		canShow,
		podcastsByWorkspace,
		markShown,
		markClicked,
		markDismissed,
		t,
		navigate,
	]);

	return null;
}
